import React, { useState, useEffect, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import "./Abilities.css";
import Ability from "../../Cardetails/Ability";

const Index = ({ abilities }) => {
    // default to first ability selected
    const [selectedIndex, setSelectedIndex] = useState(0);
    // responsive/mobile view state: 'both' (desktop), 'list' (mobile list), 'detail' (mobile detail)
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const [viewMode, setViewMode] = useState(typeof window !== 'undefined' && window.innerWidth <= 768 ? 'list' : 'both');

    // keep a sorted copy of abilities (alphabetical by name)
    const sortedAbilities = useMemo(() => {
        if (!abilities) return [];
        return [...abilities].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [abilities]);

    // reset selection when abilities list changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [abilities]);

    // watch for window size changes to toggle mobile behavior
    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            setViewMode(mobile ? 'list' : 'both');
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // When entering/exiting mobile detail view, add/remove a class on the nearest .index-component
    useEffect(() => {
        if (!isMobile) return;
        const container = document.querySelector('.index-component');
        if (!container) return;
        if (viewMode === 'detail') {
            container.classList.add('hide-index-arrows');
        } else {
            container.classList.remove('hide-index-arrows');
        }
        // cleanup when unmount
        return () => container.classList.remove('hide-index-arrows');
    }, [isMobile, viewMode]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            // on mobile, swiping left from list -> show detail (if selection exists)
            if (isMobile && viewMode === 'list' && sortedAbilities.length) {
                setViewMode('detail');
            }
        },
        onSwipedRight: () => {
            // on mobile, swiping right from detail -> go back to list
            if (isMobile && viewMode === 'detail') {
                setViewMode('list');
            }
        },
    });

    const SvgFilters = () => (
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" style={{ height: 0, width: 0, position: 'absolute' }}>
            <defs>
                <filter id="shadow" height="130%">
                    <feDropShadow dx="2" dy="2" stdDeviation="2" floodColor="black" />
                </filter>
            </defs>
        </svg>
    );

    return (
        <div className="abilities-component parent" {...swipeHandlers}>
            <SvgFilters />
            <div className="container abilities-two-col">
                <br/>
                <h1 className="text-white text-center">Abilities</h1>
                <hr className="text-white w-50 mx-auto"/>

                {/* Two column layout: left = list, right = detail */}
                <div className="row">
                    {/* Mobile: show list or detail full-width. Desktop: two-column layout */}
                    {isMobile ? (
                        viewMode === 'list' ? (
                            <div className="col-12 abilities-list">
                                {sortedAbilities.map((ability, index) => (
                                    <div
                                        key={ability.name}
                                        className={`ability-row ${index === selectedIndex ? 'selected' : ''}`}
                                        onClick={() => { setSelectedIndex(index); setViewMode('detail'); }}
                                    >
                                        <img
                                            className="ability-row-icon"
                                            src={ability.image || `/assets/icons/abilities/${ability.name}.png`}
                                            alt={ability.name}
                                        />
                                        <div className="ability-row-name text-white">{ability.name}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="col-12 abilities-detail abilities-mobile-detail">
                                <div className="mobile-detail-header d-flex align-items-center">
                                    <button className="btn btn-link text-white me-2" onClick={() => setViewMode('list')} aria-label="Back to list">← Back</button>
                                    <h2 className="m-0 text-white">{sortedAbilities[selectedIndex]?.name || 'Ability'}</h2>
                                </div>
                                {sortedAbilities[selectedIndex] ? (
                                    <Ability ability={sortedAbilities[selectedIndex].name} abilities={sortedAbilities} />
                                ) : (
                                    <div className="text-white">No ability selected.</div>
                                )}
                            </div>
                        )
                    ) : (
                        <>
                            <div className="col-12 col-md-4 abilities-list">
                                {sortedAbilities.map((ability, index) => (
                                    <div
                                        key={ability.name}
                                        className={`ability-row ${index === selectedIndex ? 'selected' : ''}`}
                                        onClick={() => setSelectedIndex(index)}
                                    >
                                        <img
                                            className="ability-row-icon"
                                            src={ability.image || `/assets/icons/abilities/${ability.name}.png`}
                                            alt={ability.name}
                                        />
                                        <div className="ability-row-name text-white">{ability.name}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="col-12 col-md-8 abilities-detail">
                                {sortedAbilities[selectedIndex] ? (
                                    <Ability ability={sortedAbilities[selectedIndex].name} abilities={sortedAbilities} />
                                ) : (
                                    <div className="text-white">No ability selected.</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Index;
